<template>
  <page-template :show-loader="false">
    <template #pageHeadline>
      {{ t('purgeCdnCache.pageHeadline') }}
    </template>
    <template #pageContent>
      <div v-if="isProvidersLoading" class="rbz-card white providers-loading-card">
        <rbz-loader
          :loading="!!isProvidersLoading"
          :data-exists="providers.length > 0"
          height="100%"
        />
      </div>
      <template v-else-if="providers.length">
        <div class="rbz-card white purge-card">
          <div class="purge-card-title">
            <span>
              {{ t('purgeCdnCache.purgeCard.title') }}
              <span
                v-b-tooltip="
                  isAwsProvider
                    ? $t('purgeCdnCache.purgeCard.titleAdditionalInfoTooltipAws')
                    : $t('purgeCdnCache.purgeCard.titleAdditionalInfoTooltipGcp')
                "
              >
                <icon-info-small color="dusty-gray" class="info-icon is-vcentered" />
              </span>
            </span>
          </div>
          <div class="purge-card-content">
            <div class="rbz-columns">
              <template v-if="!isAwsProvider">
                <div class="rbz-column is-4">
                  <div class="rbz-field">
                    <label class="rbz-label">
                      {{ $t('purgeCdnCache.purgeCard.purgeOption.label') }}
                    </label>
                    <div class="radio-options-wrapper">
                      <rbz-form-radio
                        id="purge-option-all"
                        name="purge-option"
                        data-qa="purge-option-all-radio"
                        :label="$t('purgeCdnCache.purgeCard.purgeOption.radioAll')"
                        :checked="purgeOption === 'all'"
                        @input="purgeOption = 'all'"
                      />
                      <rbz-form-radio
                        id="purge-option-specific"
                        name="purge-option"
                        data-qa="purge-option-specific-radio"
                        :label="$t('purgeCdnCache.purgeCard.purgeOption.radioSpecific')"
                        @input="purgeOption = 'specific'"
                        :checked="purgeOption === 'specific'"
                      />
                    </div>
                  </div>
                </div>
                <div class="vertical-divider" v-if="purgeOption === 'specific'">
                  <layout-divider-line orientation="vertical" color="gray" />
                </div>
                <div class="rbz-column entries-column" v-if="purgeOption === 'specific'">
                  <div class="purge-entry" v-for="entry in purgeEntries" :key="entry.id">
                    <div class="purge-entry-host-wrapper">
                      <div class="rbz-field input-field">
                        <rbz-form-input
                          name="host-input"
                          class="host-input"
                          data-qa="host-input"
                          :label="$t('purgeCdnCache.purgeCard.purgeEntryGcp.host')"
                          :placeholder="$t('purgeCdnCache.purgeCard.purgeEntryGcp.host')"
                          v-model:inputValue="entry.host"
                        />
                      </div>
                      <div class="rbz-field rbz-field-toggle">
                        <rbz-form-toggle
                          v-model:buttonValue="entry.specificPath"
                          :label="$t('purgeCdnCache.purgeCard.purgeEntry.addSpecificPathToggle')"
                          class="specific-path-toggle"
                          data-qa="specific-path-toggle"
                        />
                      </div>
                    </div>
                    <div v-if="entry.specificPath" class="rbz-field input-field">
                      <rbz-form-input
                        name="path-input"
                        class="path-input"
                        data-qa="path-input"
                        :label="$t('purgeCdnCache.purgeCard.purgeEntry.path')"
                        :placeholder="$t('purgeCdnCache.purgeCard.purgeEntry.path')"
                        v-model:inputValue="entry.path"
                      />
                    </div>
                    <div class="align-items-center">
                      <rbz-form-button
                        class="remove-entry-button"
                        data-qa="remove-entry-button"
                        variant="tertiary"
                        icon="IconDelete"
                        v-b-tooltip="$t('purgeCdnCache.purgeCard.purgeEntry.removeEntryTooltip')"
                        @button-click="removePurgeEntry(entry.id)"
                      />
                    </div>
                  </div>
                  <rbz-form-button
                    :label="$t('purgeCdnCache.purgeCard.purgeEntryGcp.newEntry')"
                    variant="secondary"
                    class="new-purge-entry-button"
                    data-qa="add-purge-entry-button"
                    @button-click="addPurgeEntry()"
                  />
                </div>
              </template>
              <template v-else>
                <div class="rbz-column is-4">
                  <div class="rbz-field">
                    <rbz-dropdown
                      :items="providers.map((item) => [item.distribution_id, item.name])"
                      :disabled="providers.length === 1"
                      v-model:value="selectedProvider"
                      @update:value="clearPurgeEntries()"
                      :label="$t('purgeCdnCache.purgeCard.selectCdn')"
                      class="provider-selection"
                      data-qa="provider-dropdown"
                    />
                  </div>
                </div>
                <div class="vertical-divider">
                  <layout-divider-line orientation="vertical" color="gray" />
                </div>
                <div class="rbz-column is-4">
                  <div class="rbz-field">
                    <label class="rbz-label">
                      {{ $t('purgeCdnCache.purgeCard.alternativeDomainNames') }}
                    </label>
                    <rbz-form-textarea
                      name="alternativeDomainNames"
                      classes="alternative-domain-names"
                      data-qa="alternative-domain-names"
                      v-model:inputValue="getAlternativeDomainNames"
                      disabled
                      rows="8"
                    />
                  </div>
                </div>
                <div class="vertical-divider">
                  <layout-divider-line orientation="vertical" color="gray" />
                </div>
                <div class="purge-entry-aws-wrapper rbz-column is-5">
                  <div class="rbz-columns">
                    <div class="rbz-column is-5">
                      <div class="rbz-field rbz-field-toggle">
                        <rbz-form-toggle
                          v-model:buttonValue="awsSpecificPath"
                          @update:button-value="toggleAwsPurgeEntry($event)"
                          :label="$t('purgeCdnCache.purgeCard.purgeEntry.addSpecificPathToggle')"
                          class="specific-path-toggle"
                          data-qa="specific-path-toggle"
                        />
                      </div>
                    </div>
                    <div class="purge-entry-aws-column rbz-column is-7">
                      <div class="purge-entry" v-for="entry in purgeEntries" :key="entry.id">
                        <div class="rbz-field input-field">
                          <rbz-form-input
                            name="path-input"
                            class="path-input"
                            data-qa="path-input"
                            :label="$t('purgeCdnCache.purgeCard.purgeEntry.path')"
                            :placeholder="$t('purgeCdnCache.purgeCard.purgeEntry.path')"
                            v-model:inputValue="entry.path"
                          />
                        </div>
                        <div class="align-items-center">
                          <rbz-form-button
                            class="remove-entry-button"
                            data-qa="remove-entry-button"
                            variant="tertiary"
                            icon="IconDelete"
                            v-b-tooltip="
                              $t('purgeCdnCache.purgeCard.purgeEntry.removeEntryTooltip')
                            "
                            @button-click="removePurgeEntry(entry.id)"
                          />
                        </div>
                      </div>
                      <div class="purge-entry-button">
                        <rbz-form-button
                          v-if="purgeEntries.length"
                          :label="$t('purgeCdnCache.purgeCard.purgeEntryAws.newEntry')"
                          variant="secondary"
                          class="new-purge-entry-button"
                          data-qa="add-purge-entry-button"
                          @button-click="addPurgeEntry()"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </template>
              <div class="rbz-column is-narrow" v-if="purgeOption === 'all' && !isAwsProvider">
                <rbz-form-button
                  :label="$t('purgeCdnCache.initPurgeButton')"
                  variant="primary"
                  icon="IconReload"
                  @button-click="initPurge()"
                  class="init-purge-button"
                  data-qa="init-purge-button"
                />
              </div>
            </div>
          </div>
        </div>
        <div
          class="init-purge-button-row"
          v-if="(purgeOption === 'specific' && !isAwsProvider) || isAwsProvider"
        >
          <rbz-form-button
            :label="$t('purgeCdnCache.initPurgeButton')"
            variant="primary"
            icon="IconReload"
            @button-click="initPurge()"
            :disabled="initPurgeDisabled"
            class="init-purge-button"
            data-qa="init-purge-button"
          />
        </div>
      </template>
      <div class="rbz-card white no-cdn-card" v-else>
        <div class="no-cdn-card-message">
          <div class="rbz-text-h4">
            {{ t('purgeCdnCache.noCdnCard.noCdnMessage') }}
          </div>
          <div>
            <div>
              {{ t('purgeCdnCache.noCdnCard.contactSupportMessage') }}
            </div>
            <div>support@reblaze.com</div>
          </div>
        </div>
        <rbz-form-button
          :label="t('purgeCdnCache.noCdnCard.contactSupportButtonLabel')"
          link="mailto:support@reblaze.com"
          :external-link="true"
          class="contact-support-button"
          data-qa="contact-support-button"
        />
      </div>
      <div v-if="isPurgesTableLoading" class="purges-table-loading">
        <rbz-loader
          :loading="!!isPurgesTableLoading"
          :data-exists="purgesTableData.length > 0"
          height="100%"
        />
      </div>
      <div v-else-if="purgesTableData.length" class="purges-table-wrapper">
        <rbz-table
          :columns="purgesTableColumns"
          :table-data="purgesTableData"
          :show-download-button="false"
          :show-new-button="false"
          :default-sort-column-index="2"
          :show-filter-row="true"
          ref="purgesTable"
          class="purges-table"
          data-qa="purges-table"
        >
          <template #tableResultsOverride="props">
            {{ t('purgeCdnCache.purgesTable.results', { count: props.numberOfResults }) }}
          </template>
          <template #tableButtonsRightOverride>
            <rbz-form-button
              :label="$t('purgeCdnCache.purgesTable.refreshStatus')"
              variant="tertiary"
              icon="IconReload"
              @button-click="loadPurgesTableData()"
              class="refresh-status-button"
              data-qa="refresh-status-button"
            />
          </template>
          <template #statusCell="cellProps">
            <div :class="`status-cell-wrapper ${getStatusColor(cellProps.row.status)}`">
              {{ cellProps.row.status }}
            </div>
          </template>
        </rbz-table>
      </div>
    </template>
  </page-template>
</template>

<script setup lang="ts">
import RbzFormButton from '@/components/RbzFormButton.vue';
import RbzTable from 'ui-component-library/rbz/components/RbzTable.vue';
import { GenericObject } from '@/types';
import { RbzTableColumnOptions } from 'ui-component-library/rbz/types';
import { computed, onBeforeMount, ref, watch } from 'vue';
import DateTimeUtils from 'ui-component-library/helpers/DateTimeUtils';
import RequestsUtils from '@/helpers/RequestsUtils';
import { useI18n } from 'vue-i18n';
import LayoutDividerLine from 'ui-component-library/rbz/components/LayoutDividerLine.vue';
import RbzFormRadio from '@/components/RbzFormRadio.vue';
import RbzFormTextarea from '@/components/RbzFormTextarea.vue';
import RbzFormInput from '@/components/RbzFormInput.vue';
import RbzDropdown from '@/components/RbzDropdown.vue';
import RbzFormToggle from '@/components/RbzFormToggle.vue';
import DatasetsUtils from '@/helpers/DatasetsUtils';
import RbzLoader from '@/components/RbzLoader.vue';
import IconInfoSmall from 'ui-component-library/assets/icons/IconInfoSmall.vue';
import PageTemplate from '@/views/PageTemplate.vue';
import _ from 'lodash';
import { useUserStore } from '@/store/UserStore';

const { t } = useI18n();

type PurgeEntry = {
  id: string;
  host: string;
  specificPath: boolean;
  path: string;
};

type PurgeData = {
  host: string;
  path: string;
  distribution_id?: string;
};

const userStore = useUserStore();

onBeforeMount(() => {
  loadProviders();
  loadPurgesTableData();
});

const providersLoadingCounter = ref(null);
const isProvidersLoading = computed(() => {
  return providersLoadingCounter.value === null || providersLoadingCounter.value > 0;
});
const setProvidersLoadingStatus = (isLoading: boolean) => {
  if (isLoading) {
    providersLoadingCounter.value++;
  } else {
    providersLoadingCounter.value--;
  }
};
const providers = ref([]);
const providerType = ref(null);
const selectedProvider = ref(null);
const selectedProviderData = computed(() => {
  return _.find(providers.value, (provider: GenericObject) => {
    return provider.distribution_id === selectedProvider.value;
  });
});
const loadProviders = async () => {
  setProvidersLoadingStatus(true);
  const response = await RequestsUtils.sendRequest({
    host: userStore.getUserPlanet(),
    methodName: 'GET',
    url: `purge-cdn-cache/providers`,
  });
  providers.value = response?.data?.items || [];
  providerType.value = providers.value?.[0]?.provider;
  selectedProvider.value = providers.value?.[0]?.distribution_id;
  setProvidersLoadingStatus(false);
};

const awsSpecificPath = ref(false);
const isAwsProvider = computed((): boolean => {
  return providerType.value === 'aws';
});

const getAlternativeDomainNames = computed(() => {
  return _.join(selectedProviderData.value.alternate_domain_names, '\n');
});

const toggleAwsPurgeEntry = (value: boolean) => {
  if (value) {
    addPurgeEntry();
    purgeOption.value = 'specific';
  } else {
    clearPurgeEntries();
    purgeOption.value = 'all';
  }
};

const initPurgeDisabled = computed(() => {
  if (isAwsProvider.value) {
    if (!awsSpecificPath.value) {
      return false;
    }
    return purgeEntries.value.some((entry: PurgeEntry) => {
      return !entry.path;
    });
  } else {
    if (!purgeEntries.value.length) {
      return true;
    }
    return purgeEntries.value.some((entry: PurgeEntry) => {
      return !entry.host && (!entry.specificPath || (entry.specificPath && !entry.path));
    });
  }
});
const initPurge = async () => {
  setPurgesTableLoadingStatus(true);
  const data = [];
  if (purgeOption.value === 'all') {
    const purgeData: PurgeData = {
      host: 'all',
      path: 'all',
    };
    if (isAwsProvider.value) {
      purgeData.distribution_id = selectedProvider.value;
    }
    data.push(purgeData);
  } else {
    purgeEntries.value.forEach((entry: PurgeEntry) => {
      let purgeData: PurgeData;
      if (isAwsProvider.value) {
        purgeData = {
          host: 'all',
          path: entry.path,
          distribution_id: selectedProvider.value,
        };
      } else {
        purgeData = {
          host: entry.host || 'all',
          path: entry.specificPath ? entry.path : 'all',
        };
      }
      data.push(purgeData);
    });
  }
  const response = await RequestsUtils.sendRequest({
    host: userStore.getUserPlanet(),
    methodName: 'POST',
    url: `purge-cdn-cache/purge`,
    data: data,
    displayServerFailureMessage: true,
  });
  if (response) {
    purgeOption.value = 'all';
    purgeEntries.value = [];
    await loadPurgesTableData();
  }
  setPurgesTableLoadingStatus(false);
};

const purgeOption = ref('all');
const purgeEntries = ref<PurgeEntry[]>([]);
const addPurgeEntry = () => {
  purgeEntries.value.push({
    id: DatasetsUtils.generateUUID2(),
    host: '',
    specificPath: false,
    path: '',
  });
};
const removePurgeEntry = (id: PurgeEntry['id']) => {
  purgeEntries.value = purgeEntries.value.filter((entry) => {
    return entry.id !== id;
  });
  if (!purgeEntries.value.length) {
    purgeOption.value = 'all';
  }
};

const clearPurgeEntries = () => {
  purgeEntries.value = [];
};

watch(
  purgeEntries,
  (newValue: PurgeEntry[]) => {
    if (!newValue.length) {
      awsSpecificPath.value = false;
    }
  },
  { deep: true }
);

const purgesTableData = ref([]);
const purgesTableLoadingCounter = ref(null);
const isPurgesTableLoading = computed(() => {
  return purgesTableLoadingCounter.value === null || purgesTableLoadingCounter.value > 0;
});
const setPurgesTableLoadingStatus = (isLoading: boolean) => {
  if (isLoading) {
    purgesTableLoadingCounter.value++;
  } else {
    purgesTableLoadingCounter.value--;
  }
};
const loadPurgesTableData = async () => {
  setPurgesTableLoadingStatus(true);
  const response = await RequestsUtils.sendRequest({
    host: userStore.getUserPlanet(),
    methodName: 'GET',
    url: `purge-cdn-cache/purges`,
  });
  purgesTableData.value = response?.data?.items || [];
  setPurgesTableLoadingStatus(false);
};
const purgesTableColumns = computed<RbzTableColumnOptions[]>(() => {
  return [
    {
      id: 'host',
      title: isAwsProvider.value
        ? t('purgeCdnCache.purgesTable.cdn')
        : t('purgeCdnCache.purgesTable.host'),
      fieldNames: ['target'],
      displayFunction: (item: GenericObject) => {
        if (isAwsProvider.value) {
          return item.target.name;
        } else {
          const isAll = item.target.host === 'all';
          if (isAll) {
            return t('purgeCdnCache.purgesTable.allHosts');
          }
          return item.target.host;
        }
      },
      isSortable: true,
      isSearchable: true,
      cellContentClasses: 'ellipsis',
    },
    {
      id: 'path',
      title: t('purgeCdnCache.purgesTable.path'),
      fieldNames: ['target'],
      displayFunction: (item: GenericObject) => {
        const isAll = item.target.path === 'all';
        if (isAll) {
          return t('purgeCdnCache.purgesTable.allPaths');
        }
        return item.target.path;
      },
      isSortable: true,
      isSearchable: true,
      cellContentClasses: 'ellipsis',
    },
    {
      id: 'datetime',
      title: t('purgeCdnCache.purgesTable.datetime'),
      fieldNames: ['timestamp'],
      isSortByOriginalValue: true,
      displayFunction: (item: GenericObject) => {
        const date = new Date(item['timestamp']);
        const adjustedDate = DateTimeUtils.adjustDateToTimezone(date);
        return DateTimeUtils.isoToNowCuriefenseFormat(adjustedDate);
      },
      tooltipFunction: (item: GenericObject) => {
        const date = new Date(item['timestamp']);
        return `${DateTimeUtils.isoToNowFullCuriefenseFormat(date)} UTC`;
      },
      tooltipFunctionAlwaysDisplayed: true,
      isSortable: true,
      isSearchable: true,
      cellContentClasses: 'ellipsis',
    },
    {
      id: 'status',
      title: t('purgeCdnCache.purgesTable.status'),
      fieldNames: ['status'],
      displaySlotName: 'statusCell',
      isSortable: true,
      isSearchable: true,
      cellContentClasses: 'ellipsis',
    },
  ];
});
const getStatusColor = (status: string) => {
  return status.toLowerCase();
};
</script>

<style scoped lang="scss">
@use 'ui-component-library/assets/styles/colors' as colors;
@use 'sass:map';

.providers-loading-card,
.purges-table-loading {
  height: 220px;
  position: relative;
}

.purge-card {
  .purge-card-title {
    align-items: center;
    display: flex;
    gap: 4px;
    height: 48px;
    padding: 12px 16px 12px 24px;
  }

  .purge-card-content {
    display: flex;
    padding: 24px;
    align-items: flex-start;

    .rbz-columns {
      gap: 40px;
      justify-content: space-between;
    }

    .entries-column {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .radio-options-wrapper {
      display: flex;
      gap: 16px;
    }
  }

  .purge-entry {
    align-items: center;
    display: flex;
    gap: 40px;
    height: 87px;
    justify-content: space-between;

    .purge-entry-host-wrapper {
      display: flex;
      gap: 16px;
      width: 50%;
    }

    .input-field {
      flex-grow: 1;
    }
  }

  .purge-entry-aws-wrapper {
    .rbz-field-toggle {
      min-height: 62px;
    }

    :deep(.toggle-wrapper) {
      min-width: 48px;
    }

    .purge-entry {
      align-items: flex-start;
    }

    .purge-entry-button {
      display: flex;
      justify-content: flex-end;
    }
  }

  .purge-entry-aws-column {
    .purge-entry:nth-last-child(2) {
      margin-bottom: 24px;
    }
  }
}

.init-purge-button-row {
  align-items: flex-end;
  display: flex;
  gap: 16px;
  justify-content: flex-end;
  padding: 16px 0;
}

.no-cdn-card {
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 24px;
  justify-content: center;
  padding: 60px 24px;

  .no-cdn-card-message {
    align-items: center;
    display: flex;
    flex-direction: column;
    gap: 16px;
    justify-content: center;
    text-align: center;
  }
}

.purges-table {
  .status-cell-wrapper {
    align-items: center;
    border-radius: 4px;
    display: inline-flex;
    flex-direction: column;
    flex-shrink: 0;
    gap: 4px;
    height: 24px;
    justify-content: center;
    padding: 4px 8px;

    &.done {
      background-color: map.get(colors.$colors, 'jagged-ice');
      border: 1px solid map.get(colors.$colors, 'mountain-meadow');
    }

    &.in-progress {
      background-color: map.get(colors.$colors, 'karry');
      border: 1px solid map.get(colors.$colors, 'neon-carrot');
    }

    &.failed,
    &.not-available {
      background-color: map.get(colors.$colors, 'melon');
      border: 1px solid map.get(colors.$colors, 'tabasco');
    }
  }
}
</style>
